<template>
    <div>
        <DCard
            class="jobs-table"
            footer-padding
        >
            <template #default>
                <!-- In the card's main slot, so the toggle reads as part of this table's chrome. -->
                <div class="archive-band">
                    <ArchiveModeSwitch
                        :model-value="archiveMode"
                        :types="referenceTypeOptions"
                        :selected-type="filtersData.archiveReferenceType || ''"
                        :reference-id="filtersData.archiveReferenceId || ''"
                        :type-error="errors.archiveReferenceType"
                        :reference-id-error="errors.archiveReferenceId"
                        :read-failed="archiveReadFailed"
                        @update:modelValue="onArchiveToggle"
                        @update:selectedType="(value) => { filtersData.archiveReferenceType = value || '' }"
                        @update:referenceId="(value) => { filtersData.archiveReferenceId = value }"
                        @apply="applyArchiveFilters"
                    />
                </div>
                <div class="jobs-table-scroller">
                    <!--
                        One table for both modes: archive mode drops the column filters and the
                        row actions, but every cell renders through the same templates.

                        The `v-if="!archiveMode"` on each `-filter` slot below and the `key` go
                        together. DTable captures the slot set once in setup and teleports each
                        filter into `tr:nth-child(2) th:nth-child(N)`, a row `column-filter=false`
                        does not render. So the slots must be gone in archive mode (else they
                        teleport into null and blow up on unmount) and the table must remount on
                        the way back (else the empty capture stands and CSmartTable's own filter
                        inputs replace ours).

                        `archiveItems` follows the server `archive` prop rather than the toggle,
                        so the list is empty until Apply where `queueJobs.items` would still hold
                        live rows.
                    -->
                    <DTable
                        :key="archiveMode ? 'archive' : 'live'"
                        :column-filter="!archiveMode"
                        :items="archiveMode ? archiveItems : queueJobs.items"
                        :fields="fields"
                        :no-items-label="archiveMode ? archiveNoItemsLabel : undefined"
                    >
                        <template
                            v-if="!archiveMode"
                            #updated_at-filter
                        >
                            <DDateFormatInput
                                id="updated-at"
                                format="YYYY-MM-DD"
                                type="date"
                                :model-value="selectedUpdatedAt"
                                @update:modelValue="(value) => { onUpdatedAtFilterChange(value)}"
                            />
                        </template>
                        <template
                            v-if="!archiveMode"
                            #queue-filter
                        >
                            <DMultiSelect
                                id="queue-dropdown"
                                :options="queueOptionsParams"
                                selection-type="text"
                                :selected="filtersData.queue"
                                @update="(values) => onFilterUpdate('queue', values)"
                            />
                        </template>
                        <template
                            v-if="!archiveMode"
                            #action-filter
                        >
                            <DMultiSelect
                                id="action-dropdown"
                                :options="actionsOptionsParams"
                                selection-type="text"
                                :selected="filtersData.action"
                                @update="(values) => onFilterUpdate('action', values)"
                            />
                        </template>
                        <template
                            v-if="!archiveMode"
                            #requestFilter-filter
                        >
                            <DMultiSelect
                                id="filter-dropdown"
                                :options="filterOptionsParams"
                                selection-type="text"
                                :selected="filtersData.filterJob"
                                @update="(values) => onFilterUpdate('filterJob', values)"
                            />
                        </template>
                        <template
                            v-if="!archiveMode"
                            #status-filter
                        >
                            <DMultiSelect
                                id="status-dropdown"
                                :options="statusOptionsParams"
                                selection-type="text"
                                :selected="filtersData.statusJob"
                                @update="(values) => onFilterUpdate('statusJob', values)"
                            />
                        </template>
                        <template
                            v-if="!archiveMode"
                            #reference-filter
                        >
                            <!--
                                Type and id are one filter, not two. An id on its own matches
                                across reference types (id 5 is a Customer and a Contract alike)
                                and leads no index, so the server rejects it; the select is what
                                makes the id usable. Picking a type therefore does not search --
                                it unlocks the id input -- so nothing is submitted until there is
                                something to search for.
                            -->
                            <!--
                                Stacked, not side by side: this column is one of the narrowest on
                                the page, and splitting it left the select reading "Any referen"
                                and the id box too small to show a customer number.
                            -->
                            <div class="d-flex flex-column gap-1">
                                <select
                                    id="reference-type"
                                    class="form-select"
                                    :value="filtersData.referenceType"
                                    @change="(event) => onReferenceTypeChange(event.target.value)"
                                >
                                    <option value="">
                                        {{ __('administrations.queue_jobs.filters.any_reference_type') }}
                                    </option>
                                    <option
                                        v-for="type in referenceTypeOptions"
                                        :key="type"
                                        :value="type"
                                    >
                                        {{ type }}
                                    </option>
                                </select>
                                <input
                                    id="reference"
                                    class="form-control"
                                    type="number"
                                    :value="filtersData.reference"
                                    :disabled="!filtersData.referenceType"
                                    :title="!filtersData.referenceType
                                        ? __('administrations.queue_jobs.filters.reference_needs_type')
                                        : null"
                                    @change="(event) => onReferenceFilterUpdate(filtersData.referenceType, event.target.value)"
                                >
                            </div>
                        </template>

                        <template #actions="{item}">
                            <td style="display: flex; grid-gap: 5px;">
                                <DButton
                                    icon="eye"
                                    @click="showDetailModal(item)"
                                />
                                <!-- No live row left to retry, pause or cancel. -->
                                <DDropdown
                                    v-if="!archiveMode"
                                    :id="`actions_dropdown_${item.id}`"
                                    :class="'more-actions-button'"
                                    :caret="false"
                                    icon="dots-horizontal"
                                >
                                    <DDropdownItem
                                        v-if="item.status === 'retry'"
                                        :id="`action_retry_now_${item.id}`"
                                        @click="retryNow(item.id)"
                                    >
                                        {{ __('administrations.queue_jobs.actions.retry_now') }}
                                    </DDropdownItem>
                                    <DDropdownItem
                                        v-if="item.status ==='failed'"
                                        :id="`action_retry_${item.id}`"
                                        @click="retry(item.id)"
                                    >
                                        {{ __('administrations.queue_jobs.actions.retry') }}
                                    </DDropdownItem>
                                    <DDropdownItem
                                        v-if="item.status === 'queued' || item.status === 'retry'"
                                        :id="`action_pause_${item.id}`"
                                        @click="pause(item.id)"
                                    >
                                        {{ __('administrations.queue_jobs.actions.pause') }}
                                    </DDropdownItem>
                                    <DDropdownItem
                                        v-if="item.status === 'paused'"
                                        :id="`action_resume_${item.id}`"
                                        @click="resume(item.id)"
                                    >
                                        {{ __('administrations.queue_jobs.actions.resume') }}
                                    </DDropdownItem>
                                    <DDropdownItem
                                        v-if="item.status === 'queued' || item.status === 'failed' || item.status === 'retry'"
                                        :id="`action_cancel_${item.id}`"
                                        @click="cancel(item.id)"
                                    >
                                        {{ __('administrations.queue_jobs.actions.cancel') }}
                                    </DDropdownItem>
                                </DDropdown>
                            </td>
                        </template>
                        <template #reference="{item}">
                            <td>
                                <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component -->
                                <Link
                                    v-if="referenceLink(item)"
                                    :href="referenceLink(item)"
                                    v-html="referenceText(item)"
                                />
                                <!--
                                    Reference types this table has no detail route for still say
                                    what the row points at instead of rendering an empty cell.
                                    Every type a job can write is selectable in the filter, but
                                    only some have a page to link to.
                                -->
                                <span
                                    v-else
                                    v-html="referenceText(item)"
                                />
                                <!--eslint-enable-->
                            </td>
                        </template>
                        <template #last_error="{item}">
                            <td class="last-error-cell">
                                <!--
                                    Clamped rather than rendered in full. The worst errors are
                                    unbroken class paths with no spaces to wrap on, so this
                                    column grew until it squeezed every other one and forced the
                                    table to scroll. Hover shows the whole message, and the
                                    detail modal lists every attempt in full.
                                -->
                                <!-- eslint-disable-next-line vue/no-v-text -->
                                <div
                                    v-if="item.last_error"
                                    class="last-error"
                                    :title="item.last_error"
                                    v-text="item.last_error"
                                />
                            </td>
                        </template>
                        <template #available="{item}">
                            <td>
                                {{ item.status === 'failed' ? '' : item.available }}
                            </td>
                        </template>
                        <template #requestFilter="{item}">
                            <td>
                                {{ item.filter === null ? '' : item.filter }}
                            </td>
                        </template>
                    </DTable>
                </div>
            </template>
            <!--
                No reset in archive mode: it clears the live filter set, none of which is showing
                -- the lookup is the switch's own fields with its own Apply. The v-if sits on the
                slot, not the button, so DCard drops the footer (`hasFooterSlot`) rather than
                leaving an empty padded band.
            -->
            <template
                v-if="!archiveMode"
                #footer
            >
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </template>
        </DCard>
        <SimplePagination
            v-if="paginationVisible"
            :current-page="queueJobs.current_page"
            :has-previous="queueJobs.has_previous"
            :has-next="queueJobs.has_next"
            @update:activePage="changePage"
        />
        <ShowQueuesJobModal
            v-if="detailModal"
            :show-modal="detailModal"
            :queue-job="itemDetail"
            :archived="archive"
            @close-modal="closeDetailModal"
        />
    </div>
</template>

<script>
import ArchiveModeSwitch from '~@/Components/Common/Archive/ArchiveModeSwitch.vue'
import ShowQueuesJobModal from '~@/Components/Administrations/QueuesJobs/ShowQueuesJobModal'
import SimplePagination from '~@/Components/Common/ActivityLogs/SimplePagination'
import { toRaw } from 'vue'
import _ from 'lodash'

export default {
    name: 'QueueJobsIndex',
    components: {
        ArchiveModeSwitch,
        ShowQueuesJobModal,
        SimplePagination
    },
    props: {
        queueJobs: {
            type: Object,
            default: () => { return {} }
        },
        queueOptions: {
            type: Array,
            default: () => { return [] }
        },
        actionsOptions: {
            type: Array,
            default: () => { return [] }
        },
        filterOptions: {
            type: Object,
            default: () => { return {} }
        },
        statusOptions: {
            type: Array,
            default: () => { return [] }
        },
        filters: {
            type: Object,
            default: () => { return {} }
        },
        referenceTypeOptions: {
            type: Array,
            default: () => { return [] }
        },
        archive: {
            type: Boolean,
            default: false
        },
        archiveReferenceType: {
            type: String,
            default: ''
        },
        archiveReferenceId: {
            type: [Number, String],
            default: null
        },
        archiveReadFailed: {
            type: Boolean,
            default: false
        },
        errors: {
            type: Object,
            default: () => { return {} }
        }
    },
    data () {
        return {
            // Every column carries a minimum, set on the header cell via CoreUI's `_style`.
            //
            // Two reasons. Without one, the auto table layout re-derives every width from
            // whatever happens to be on the current page, so columns jump as you filter and
            // page -- and a column whose content can break anywhere (Last error) collapses
            // to a strip while a column that cannot break (Action) keeps width it does not
            // need. The minimums are sized to the header label in the longer of the two
            // locales, so "Available at" / "Verfuegbar um" stops wrapping onto two lines.
            //
            // Minimums rather than fixed widths: surplus still goes where it is most useful
            // instead of being stranded, and nothing is truncated at a width someone picked
            // by eye. They total roughly 86rem, and the table is `responsive`, so a narrow
            // viewport scrolls rather than squeezing everything illegible.
            fields: [
                { key: 'updated_at', label: this.__('administrations.queue_jobs.table.updated_at'), _style: { minWidth: '8rem' } },
                { key: 'queue', label: this.__('administrations.queue_jobs.table.queue'), _style: { minWidth: '6rem' } },
                { key: 'action', label: this.__('administrations.queue_jobs.table.action'), _style: { minWidth: '11rem' } },
                { key: 'requestFilter', label: this.__('administrations.queue_jobs.table.filter'), _style: { minWidth: '8rem' } },
                { key: 'status', label: this.__('administrations.queue_jobs.table.status'), _style: { minWidth: '7rem' } },
                { key: 'reference', label: this.__('administrations.queue_jobs.table.reference'), _style: { minWidth: '12rem' } },
                { key: 'last_error', label: this.__('administrations.queue_jobs.table.last_error'), filter: false, _style: { minWidth: '20rem' } },
                { key: 'available', label: this.__('administrations.queue_jobs.table.available_at'), filter: false, _style: { minWidth: '7.5rem' } },
                { key: 'actions', label: this.__('administrations.queue_jobs.table.actions'), filter: false, _style: { minWidth: '6.5rem' } }
            ],
            itemDetail: {},
            detailModal: false,
            // Local, not the `archive` prop -- see ArchiveModeSwitch. A rejected lookup also
            // counts as on: validation redirects to a URL with no `archive`, so the switch would
            // otherwise snap back to live and take the error messages off screen with it.
            archiveMode: this.archive ||
                Boolean(this.errors.archiveReferenceType) ||
                Boolean(this.errors.archiveReferenceId),
            // All three archive keys ride the live filter object, because every visit sends it
            // wholesale, and all three are seeded from the props. QueueMonitoringFilter::toArray()
            // emits only the live keys, so a page entered directly in archive mode (refresh,
            // bookmark, shared link) would otherwise drop the mode and its lookup on the next
            // whole-set visit -- turning a page, say. That same omission keeps an archive lookup
            // out of saved filters. The reference pair is its own, not the live
            // referenceType/reference: sharing left each mode filtering the other.
            filtersData: {
                ..._.cloneDeep(this.filters),
                archive: this.archive,
                archiveReferenceType: this.archiveReferenceType,
                archiveReferenceId: this.archiveReferenceId ?? ''
            }
        }
    },
    computed: {
        // Same gate as the rows: until an archive visit happens the props still hold the live
        // result set, so a live page count would sit under an empty archive table.
        paginationVisible () {
            if (this.archiveMode && !this.archive) {
                return false
            }

            return this.queueJobs.has_previous || this.queueJobs.has_next
        },
        // Empty until the server has actually rendered archive mode -- see the table comment.
        archiveItems () {
            return this.archive ? this.queueJobs.items : []
        },
        archiveNoItemsLabel () {
            const hasLookup = this.filtersData.archiveReferenceType && this.filtersData.archiveReferenceId

            return this.archive && hasLookup
                ? this.__('messages.archive.no_records')
                : this.__('messages.archive.reference_hint')
        },
        selectedUpdatedAt () {
            return this.filtersData.updatedAtFrom?.split(' ')[0]
        },
        queueOptionsParams () {
            return this.queueOptions.map((value) => {
                return { value, text: value, selected: this.filtersData.queue?.includes(value) }
            })
        },
        actionsOptionsParams () {
            return this.actionsOptions.map((value) => {
                return { value, text: value, selected: this.filtersData.action?.includes(value) }
            })
        },
        filterOptionsParams () {
            return Object.keys(this.filterOptions).map((key) => ({ value: key, text: this.filterOptions[key], selected: this.filtersData.filterJob?.includes(key) }))
        },
        statusOptionsParams () {
            return this.statusOptions.map((value) => {
                return { value, text: value, selected: this.filtersData.statusJob?.includes(value) }
            })
        }
    },
    methods: {
        // `only` keeps paging and filtering off the props the controller wraps in closures,
        // which Inertia then never resolves. `flash` has to stay in the list: App.vue renders
        // flash.errorMessages, so dropping it would make a rejected filter look like a button
        // that does nothing.
        visit (query, extra = {}) {
            this.$inertia.get(
                this.$route('administrations.queue.index', { _query: this.applicableFilters(query), ...extra }),
                {},
                {
                    only: [
                        'queueJobs', 'filters', 'flash', 'archive',
                        'archiveReferenceType', 'archiveReferenceId', 'archiveReadFailed'
                    ]
                }
            )
        },
        // A reference type that has been picked but not yet given an id lives in local state
        // only -- it is not a filter yet. It must not ride along on an unrelated visit: the
        // server requires the pair, so changing a queue, a date or a page while a type was
        // half-selected used to fail validation and bounce the whole filter set.
        applicableFilters (query) {
            const applicable = { ...query }

            if (applicable.referenceType && !applicable.reference) {
                applicable.referenceType = ''
                applicable.reference = ''
            }

            // The same rule for archive mode's own pair, which the FormRequest requires both
            // halves of: a type picked but not yet given an id would bounce the whole filter set
            // on the next unrelated visit.
            if (applicable.archiveReferenceType && !applicable.archiveReferenceId) {
                applicable.archiveReferenceType = ''
                applicable.archiveReferenceId = ''
            }

            return applicable
        },
        // On reads nothing yet -- the switch shows the hint until the reference pair is set.
        // Off goes straight back to the live list.
        onArchiveToggle (value) {
            this.archiveMode = value

            if (!value) {
                this.filtersData.archive = false
                this.filtersData.archiveReferenceType = ''
                this.filtersData.archiveReferenceId = ''
                this.visit({ ...this.filtersData, archive: false })

                return
            }

            this.filtersData.archive = true
        },
        applyArchiveFilters () {
            this.visit({ ...this.filtersData, archive: true })
        },
        onFilterUpdate (type, value) {
            const filter = _.cloneDeep(this.filtersData)
            filter[type] = value || null

            this.visit(filter, { saveFilters: true })
        },
        // Picking a type only unlocks the id input; there is nothing to search for yet, so
        // the page is not reloaded. The exception is a reference filter that is already
        // applied, where changing or clearing the type does change the result.
        onReferenceTypeChange (referenceType) {
            const wasApplied = Boolean(this.filters.referenceType) && Boolean(this.filters.reference)

            this.filtersData.referenceType = referenceType || ''

            if (!this.filtersData.referenceType) {
                this.filtersData.reference = ''
            }

            if (wasApplied) {
                this.onReferenceFilterUpdate(this.filtersData.referenceType, this.filtersData.reference)
            }
        },
        // The reference filter is applied as a pair or not at all: half of it is ambiguous,
        // unindexed and rejected by the server. Clearing either input therefore clears both,
        // rather than stranding the user with a filter that silently does nothing.
        onReferenceFilterUpdate (referenceType, reference) {
            const filter = _.cloneDeep(this.filtersData)
            const id = reference === null || reference === undefined ? '' : String(reference)

            filter.referenceType = id === '' ? '' : (referenceType || '')
            filter.reference = filter.referenceType === '' ? '' : id

            this.visit(filter, { saveFilters: true })
        },
        onUpdatedAtFilterChange (value) {
            if (value) {
                this.filtersData.updatedAtFrom = value + ' 00:00:00'
                this.filtersData.updatedAtTo = value + ' 23:59:59'
            } else {
                this.filtersData.updatedAtFrom = this.filtersData.updatedAtTo = null
            }

            this.visit(this.filtersData, { saveFilters: true })
        },
        resetFilters () {
            const filters = toRaw(this.filtersData)
            filters.updatedAtFrom = null
            filters.updatedAtTo = null
            filters.queue = null
            filters.action = null
            filters.filterJob = null
            filters.statusJob = null
            filters.referenceType = ''
            filters.reference = ''

            this.filtersData = filters

            // defaultFilters makes the server ignore the query and return the seeded set;
            // filtersData is still sent so the form does not flash the old values first.
            // (It used to send `this.filter`, which is not a property on this component.)
            this.visit(filters, { saveFilters: true, defaultFilters: true })
        },
        referenceText (item) {
            try {
                if (item.reference_type.endsWith('\\Contract')) {
                    return `Contract(${item.job_reference?.id})`
                }
                if (item.reference_type.endsWith('\\Customer')) {
                    let html = ''
                    html += item.job_reference?.company ? `<span>${item.job_reference.company}</span>` : ''
                    html += item.job_reference?.company ? '<br>' : ''
                    html += '<span>'
                    html += item.job_reference?.firstname ? `${item.job_reference.firstname}` : ''
                    html += item.job_reference?.lastname ? ` ${item.job_reference.lastname}` : ''
                    html += '</span><br>'
                    html += item.job_reference?.id ? `<span>(${item.job_reference.id})</span>` : ''
                    return html
                }
                if (item.reference_type.endsWith('\\Invoice')) {
                    return `Invoice(${item.job_reference?.id})`
                }
                if (item.reference_type.endsWith('\\CreditNote')) {
                    return `Credit Note(${item.job_reference?.id})`
                }
                if (item.reference_type.endsWith('\\DunningLetter')) {
                    return `Dunning Letter(${item.job_reference?.id})`
                }
                if (item.reference_type.endsWith('\\InvoiceItem')) {
                    return `Invoice(${item.job_reference?.invoice_id}) Item ID(${item.job_reference?.id})`
                }
                if (item.reference_type.endsWith('\\ProductPlanImport')) {
                    return `ProductPlanImport(${item.job_reference?.id})`
                }
            } catch {
                return ''
            }

            return this.genericReferenceText(item)
        },
        // Fallback label for reference types with no bespoke rendering above. The class name
        // is stripped to word characters before it reaches v-html: it comes from the
        // `reference` column rather than user input, but nothing here needs to be markup.
        genericReferenceText (item) {
            const shortName = String(item.reference_type ?? '').split('\\').pop().replace(/[^\w]/g, '')

            if (!shortName) {
                return ''
            }

            return item.job_reference?.id ? `${shortName}(${item.job_reference.id})` : shortName
        },
        referenceLink (item) {
            try {
                if (item.reference_type.endsWith('\\Contract')) {
                    return this.contractLink(item)
                }
                if (item.reference_type.endsWith('\\Customer')) {
                    return this.customerLink(item)
                }
                if (item.reference_type.endsWith('\\Invoice')) {
                    return this.invoiceLink(item)
                }
                if (item.reference_type.endsWith('\\CreditNote')) {
                    return this.creditNoteLink(item)
                }
                if (item.reference_type.endsWith('\\DunningLetter')) {
                    return this.invoiceLink(item)
                }
                if (item.reference_type.endsWith('\\InvoiceItem')) {
                    return this.invoiceLink(item)
                }
                if (item.reference_type.endsWith('\\ProductPlanImport')) {
                    return this.productPlanImportLink(item)
                }
            } catch {
                return ''
            }
        },
        contractLink (item) {
            return this.$route('customers.customers.contracts.index', {
                customer: item.job_reference?.customer_id ?? '',
                contract: item.job_reference?.id ?? ''
            })
        },
        invoiceLink (item) {
            return this.$route('customers.customers.invoices.show', {
                customer: item.customer_id ?? item.job_reference?.customer_id,
                invoice: item.job_reference.invoice_id ?? item.job_reference?.id
            })
        },
        creditNoteLink (item) {
            return this.$route('customers.customers.credit-notes.show', {
                customer: item.job_reference?.customer_id ?? '',
                credit_note: item.job_reference?.id
            })
        },
        customerLink (item) {
            return this.$route('customers.customers.show', {
                customer: item.job_reference?.id ?? ''
            })
        },
        productPlanImportLink (item) {
            if (item.job_reference?.type === 'addon') {
                return this.$route('products.product-addons.download-upload')
            }
            return this.$route('products.products.download-upload')
        },
        // Turning a page must not persist the filter set, so no saveFilters here. The query
        // is cloned rather than mutated in place: the previous version wrote `page` onto
        // filtersData, so returning to page 1 left a stale page number behind on the next
        // filter change.
        changePage (page) {
            const query = _.cloneDeep(this.filtersData)

            if (page !== 1) {
                query.page = page
            }

            this.visit(query)
        },
        retry (id) {
            this.$inertia.post(this.$route('administrations.queue.retry', { jobRequest: id }))
        },
        retryNow (id) {
            this.$inertia.post(this.$route('administrations.queue.retryNow', { jobRequest: id }))
        },
        pause (id) {
            this.$inertia.post(this.$route('administrations.queue.pause', { jobRequest: id }))
        },
        resume (id) {
            this.$inertia.post(this.$route('administrations.queue.resume', { jobRequest: id }))
        },
        cancel (id) {
            this.$inertia.post(this.$route('administrations.queue.cancel', { jobRequest: id }))
        },
        showDetailModal (item) {
            this.itemDetail = item
            this.detailModal = true
        },
        closeDetailModal () {
            this.itemDetail = null
            this.detailModal = false
        }
    }
}
</script>

<style lang="scss" scoped>
.more-actions-button {
    background: var(--colorSecondary);
    color: var(--colorSecondaryText);
    border-radius: 5px;
}
.more-actions-button:hover {
    background: var(--colorSecondaryHover);
}
.more-actions-button:active {
    background: var(--colorSecondaryActive);
}
.jobs-table {
    .c-multi-select-options {
        width: auto !important;
    }
}
// `main` has no padding on this page, so the band pays for its own; the rule matches the
// card's header/footer borders.
.archive-band {
    padding: 15px;
    border-bottom: 1px solid var(--colorAppBorder);
}
// The table must never push past the content area, at any viewport width.
//
// CoreUI's own .table-responsive cannot do this here: app.scss forces
// `.table-responsive { overflow: visible !important }` app-wide, which is why passing
// CoreUI's `responsive` prop had no effect at all. And the ancestors above this point
// (#app and .d-main-wrapper) are `overflow: hidden`, so anything too wide is clipped and
// unreachable rather than scrollable -- which is what "escaping the page" looks like.
//
// So the scroller is local. Column minimums stay in rem, because they exist to keep header
// labels on one line and that is a text-size question; this is what makes those minimums
// safe to hold at every width instead of having to be traded away on small screens.
.jobs-table-scroller {
    // Height is deliberately left to content. `overflow-x: auto` makes the computed
    // `overflow-y` `auto` too, but with an auto height scrollHeight equals clientHeight, so
    // this adds no second vertical scrollbar -- the page keeps the single one that
    // .d-main has always owned (App.vue sets `overflow-y: scroll` on it).
    overflow-x: auto;

    // Room for the header filter dropdowns to open into. CoreUI positions them with Popper
    // using `boundary: 'clippingParents'`, so this scroller becomes their boundary: without
    // a floor, filtering down to one or two rows would leave a box too short to open them
    // in. Cheaper and more predictable than a padding/negative-margin reserve.
    min-height: var(--multi-select-scroller-min-height);
}
// The floor for this column lives with the others in `fields`; the ceiling stays here,
// because it is the one column whose content is unbounded and would otherwise take the whole
// table -- which is what it did before any of this.
.last-error-cell {
    max-width: 34rem;
}
.last-error {
    // `anywhere` is what breaks App\Jobs\... paths, which carry no spaces to wrap on;
    // `pre-line` keeps the newlines the message itself carries. Clamped to three lines, with
    // the full text on hover and every attempt in the detail modal.
    overflow: hidden;
    overflow-wrap: anywhere;
    white-space: pre-line;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
}
.search {
    padding: 7px 7px 7px 14px;
    > h6 {
        padding: 0 0 7px 0 !important;
        border-bottom: var(--colorAppBorder) solid 1px;
    }
}

</style>
